import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import debounce from 'lodash-es/debounce';
import VoiceSearch from 'components/VoiceSearch';
import SearchBarInput from 'components/SearchBar';
import { useStringFromId } from '@nykaa/intl-shared';
import { STATE } from '@nykaa/ui-components/TextField';
import { localStorageService } from 'service/storage';
import { handleManualSearch } from 'helpers/utils/search/searchSuggestion';
import {
  LAST_VISITED_SEARCH_PAGE,
  LAST_VISITED_PAGE,
} from 'constants/searchSuggestion';
import { State } from 'store/types';
import { logger } from 'logging';
import { getPlaceholderText } from 'components/Header/Search/helper';
import { getUpdatedPageType } from 'shared/helpers';

interface SearchBarProps {
  pageType: string;
  maxSearchHistoryCount: number;
  fetchSearchSuggestion: (val: any, isPro: boolean) => Promise<void>;
  updateSearchField: (val: any) => Promise<void>;
  isPro: boolean;
  hasVoiceSearch: boolean;
  showVoiceModal: boolean;
  toggleVoiceModal: () => void;
}

const SearchBar = ({
  pageType,
  maxSearchHistoryCount,
  fetchSearchSuggestion,
  updateSearchField,
  isPro,
  showVoiceModal,
  hasVoiceSearch,
  toggleVoiceModal,
}: SearchBarProps) => {
  const hasSetPlaceholder = useRef(false);
  let placeholder = useStringFromId(getPlaceholderText());
  const intialText = useRef('');
  const search = useLocation().search;
  // ? Get last page (lp) Query Param from the url
  const urlParams = new URLSearchParams(search);
  const updatedPageType = getUpdatedPageType(search, pageType);
  const prevRoute = urlParams.get(LAST_VISITED_PAGE);
  const CHECK_PREV_ROUTE = 'searchResults';
  const searchField = useSelector(
    (state: State) => state.searchSuggestions.searchField || ''
  );
  const [searchState, setSearchState] = useState('default');
  const [searchKey, setSearchKey] = useState(searchField);

  const updateSearchResultsAndField = useCallback(
    (val: string = '') => {
      updateSearchField(val);
      fetchSearchSuggestion(val, isPro);
    },
    [fetchSearchSuggestion, isPro, updateSearchField]
  );
  useEffect(() => {
    setSearchKey(searchField);
  }, [searchField]);

  const onChange = debounce((val) => {
    updateSearchResultsAndField(val);
  }, 100);

  useEffect(() => {
    if (!showVoiceModal) {
      setSearchState(STATE.focus);
    }
    // Get Latest Visited Search Page
    const lastVisitedSearchPage = localStorageService.getItem(
      LAST_VISITED_SEARCH_PAGE
    );
    if (
      lastVisitedSearchPage &&
      prevRoute === CHECK_PREV_ROUTE &&
      !hasSetPlaceholder?.current &&
      !intialText.current
    ) {
      try {
        setSearchKey(lastVisitedSearchPage);
        intialText.current = lastVisitedSearchPage;
        hasSetPlaceholder.current = true;
        onChange(lastVisitedSearchPage);
      } catch (err) {
        logger.error(err, 'Parsing "search history"');
      }
    }
  }, [onChange, prevRoute, showVoiceModal, updateSearchResultsAndField]);

  useEffect(() => {
    if (!showVoiceModal) {
      setSearchState(STATE.focus);
    }
  }, [showVoiceModal]);

  const handleSubmit = (text: string) => {
    if (text) {
      handleManualSearch(
        {
          page: updatedPageType,
          searchQuery: text,
        },
        maxSearchHistoryCount
      );
    }
  };

  const handleVoiceSearch = useCallback(
    (text: string) => {
      setSearchKey(text);
      updateSearchField(text);
      handleManualSearch(
        {
          page: updatedPageType,
          searchQuery: text,
        },
        maxSearchHistoryCount,
        true
      );
    },
    [maxSearchHistoryCount, updatedPageType, updateSearchField]
  );

  return (
    <>
      <SearchBarInput
        value={searchKey}
        state={searchState}
        placeholder={placeholder}
        showBarCodeIcon={false}
        searchCallback={(value) => onChange(value)}
        handleEnterPress={(value) => handleSubmit(value)}
      />
      {hasVoiceSearch && (
        <VoiceSearch
          showVoiceIcon={true}
          showVoiceModal={showVoiceModal}
          handleVoiceSearch={handleVoiceSearch}
          toggleVoiceModal={toggleVoiceModal}
        />
      )}
    </>
  );
};

export default SearchBar;
